import React, { useState, useEffect, useRef } from "react";
import CustomBreadcrumbs from "../../components/common/CustomBreadcrumbs";
import { toast, ToastContainer } from "react-toastify";
import { ClipboardDocumentIcon } from "@heroicons/react/24/solid";
import { AiOutlineEnter, AiOutlineDown, AiOutlineUp } from "react-icons/ai";
import api from "../../services/api";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";

const GeneratedTitlesPage = () => {
  const [titles, setTitles] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [articleData, setArticleData] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [articlePrompt, setArticlePrompt] = useState("");
  const [fetchingTitles, setFetchingTitles] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSectionExpanded, setIsSectionExpanded] = useState(false);
  const iframeRef = useRef(null);
  const articleContainerRef = useRef(null);

  const handleFetchTitles = async () => {
    if (!prompt.trim()) return;
    setArticleData(null);
    setSelectedOption(null);
    setTitles([]);
    setFetchingTitles(true);

    try {
      setIsLoading(true);
      const res = await api.post("api/open_ai/generate_seo_content", {
        type: "title",
        prompt,
      });

      if (res?.status === 200 || res?.status === 201) {
        const content = res.data.content;
        if (Array.isArray(content) && content.length > 0) {
          setTitles(content);
        } else {
          toast.warn("No titles were returned.", { autoClose: 2000 });
        }
      } else {
        toast.error("Failed to generate titles.", { autoClose: 2000 });
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to fetch titles. Please try again.", {
        autoClose: 2000,
      });
    } finally {
      setFetchingTitles(false);
      setIsLoading(false);
    }
  };

  const cleanHtmlResponse = (rawHtml) => {
    try {
      let cleaned = rawHtml.replace(/```html|```/g, "").trim();
      cleaned = cleaned.replace(/\\n/g, "\n").replace(/\\t/g, "\t");
      cleaned = cleaned.replace(/\\"/g, '"').replace(/\\'/g, "'");

      const parser = new DOMParser();
      const doc = parser.parseFromString(cleaned, "text/html");

      // Add target="_blank" to all <a> tags
      const links = doc.querySelectorAll("a");
      links.forEach((link) => {
        link.setAttribute("target", "_blank");
      });

      // Extract <style> and <body> content
      const styles = doc.querySelector("style")?.textContent || "";
      const bodyContent = doc.body.innerHTML;

      // Return a string combining style and content
      return `<style>${styles}</style>${bodyContent}`;
    } catch (e) {
      console.error("Failed to parse HTML:", e);
      return rawHtml;
    }
  };

  const handleGetArticle = async () => {
    setIsLoading(true);

    try {
      const res = await api.post("api/open_ai/generate_seo_content", {
        type: "article",
        prompt:
          articlePrompt +
          " with style, inline CSS and generate an HTML document. Return only the HTML, No explanation or extra text at the start.",
      });

      if (res?.status === 200 || res?.status === 201) {
        const rawHtml = res.data.content;
        const cleanedHtml = cleanHtmlResponse(rawHtml);
        setArticleData(cleanedHtml);
      } else {
        setArticleData(null);
        toast.warn("No articles were returned.", { autoClose: 2000 });
      }
    } catch (err) {
      console.error(err?.response?.data?.message || "Error fetching article:", err);
      toast.error("Error fetching article", { autoClose: 2000 });
    } finally {
      setIsLoading(false);
    }
  };

  // Render the article content inside a shadow DOM
  useEffect(() => {
    if (articleData && articleContainerRef.current) {
      const container = articleContainerRef.current;

      // Check if a shadow root already exists
      let shadowRoot = container.shadowRoot;
      if (!shadowRoot) {
        // Create a shadow root if it doesn't exist
        shadowRoot = container.attachShadow({ mode: "open" });
      }

      // Inject the cleaned HTML into the shadow root
      shadowRoot.innerHTML = articleData;
    }
  }, [articleData]);

  const handleCopy = (content, label) => {
    if (!content) {
      toast.error("No content to copy", { autoClose: 2000 });
      return;
    }
    navigator.clipboard.writeText(content);
    toast.success(`${label} copied to clipboard!`, { autoClose: 2000 });
  };

  const handleClear = () => {
    setPrompt("");
    setSelectedOption(null);
    setTitles([]);
  };

  useEffect(() => {
    const container = articleContainerRef.current;
    if (!container) return;

    const handleClick = (e) => {
      const btn = e.target.closest(".copy-btn");
      if (btn) {
        const text = btn.getAttribute("data-copy-text");
        if (text) {
          navigator.clipboard.writeText(text).then(() => {
            toast.success("Copied to clipboard!", { autoClose: 1500 });
            btn.innerText = "✅";
            btn.style.fontSize = "20px";
            setTimeout(() => {
              btn.style.fontSize = "16px";
            }, 1000);
          });
        }
      }
    };

    container.addEventListener("click", handleClick);
    return () => container.removeEventListener("click", handleClick);
  }, [articleData]);

  const iframeHCopy = () => {
    const iframe = iframeRef.current;
    if (iframe && iframe.contentWindow && iframe.contentWindow.document) {
      const iframeDocument = iframe.contentWindow.document;
      const text = iframeDocument.body.innerText;
      navigator.clipboard
        .writeText(text)
        .then(() => {
          toast.success("Content copied to clipboard!", {
            autoClose: 2000,
          });
        })
        .catch((err) => {
          toast.error("Failed to copy!", {
            autoClose: 2000,
          });
          console.error("Failed to copy: ", err);
        });
    }
  };
  return (
    <div className="px-6 pt-4">
      <ToastContainer />
      <CustomBreadcrumbs
        crumbs={[
          { label: "Home", link: "/" },
          { label: "Generate Content", link: "" },
        ]}
      />

      {/* Title Prompt */}
      <div className="sticky top-0 bg-white z-10 py-2 mb-2 shadow-sm px-4 mt-4 rounded-md w-full lg:w-1/2">
        <div
          className="flex items-center justify-between cursor-pointer"
          onClick={() => setIsSectionExpanded(!isSectionExpanded)}
        >
          <h2 className="text-lg font-semibold">Generate Titles</h2>
          {isSectionExpanded ? (
            <AiOutlineUp className="w-5 h-5 text-gray-800 font-bold" />
          ) : (
            <AiOutlineDown className="w-5 h-5 text-gray-800 font-bold" />
          )}
        </div>
        {isSectionExpanded && (
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 mt-4">
            <input
              type="text"
              className="h-[50px] w-full border border-gray-300 rounded px-2 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter a prompt to generate titles..."
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleFetchTitles();
              }}
            />
            <button
              onClick={handleFetchTitles}
              className="btnPrimary ml-2 h-[40px]"
              disabled={!prompt || fetchingTitles}
            >
              <AiOutlineEnter className="w-5 h-5" />
              {fetchingTitles ? "Fetching..." : "Search"}
            </button>
            <button
              onClick={handleClear}
              className="btnPrimary ml-0 bg-gray-500 h-[40px]"
            >
              Clear
            </button>
          </div>
        )}
      </div>

      {isLoading && (
        <Box sx={{ width: "100%" }} className="px-4 py-2">
          <LinearProgress />
        </Box>
      )}

      {titles.length > 0 && (
        <div className="flex flex-wrap gap-2 bg-white p-2 rounded-md shadow-md mb-2 text-sm">
          {titles.map((title, index) => (
            <div
              key={index}
              className={`flex items-center gap-2 border border-gray-300 rounded px-2 py-2 bg-gray-50 ${
                selectedOption === title ? "border-blue-500" : ""
              }`}
            >
              <span
                className="cursor-pointer text-blue-700 font-medium"
                onClick={() => {
                  setSelectedOption(title);
                  setArticleData(null);
                }}
              >
                {title}
              </span>
              <ClipboardDocumentIcon
                onClick={() => handleCopy(title, "Title")}
                className="w-4 h-4 text-black hover:text-gray-600 cursor-pointer"
              />
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1  gap-4 mb-2 w-2/3">
        {/* Left Column */}
        <div className="p-4 bg-white shadow-md rounded-md max-h-[400px] overflow-y-auto">
          <div className="flex items-center justify-between gap-4">
            <input
              type="text"
              className="h-[50px] w-full border border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter custom prompt to generate content..."
              value={articlePrompt}
              onChange={(e) => setArticlePrompt(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleGetArticle();
              }}
            />
            <div className="flex gap-4">
              <button
                onClick={handleGetArticle}
                className="btnPrimary h-[40px]"
                disabled={!articlePrompt}
              >
                <AiOutlineEnter className="w-5 h-5" />
                Search
              </button>
              <button
                onClick={() => {
                  setArticlePrompt("");
                  setArticleData("");
                }}
                className="btnPrimary bg-gray-500 h-[40px]"
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-1 gap-4 mb-4 pb-4">
        <div className="p-4 bg-white shadow-md rounded-md overflow-y-auto mb-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold mb-4">Content</h2>
            {articleData && (
              <button
                /*  onClick={() => {
                  // Parse the HTML and extract plain text
                  const parser = new DOMParser();
                  const doc = parser.parseFromString(articleData, "text/html");
                  const plainText = doc.body.textContent || "";

                  navigator.clipboard.writeText(plainText).then(() => {
                    toast.success("Content copied to clipboard!", {
                      autoClose: 2000,
                    });
                  });
                }} */
                onClick={iframeHCopy}
                className="btnPrimary bg-blue-500 text-white px-4 py-2 rounded-md"
              >
                <ClipboardDocumentIcon
                  title="Copy Content"
                  className="w-4 h-4 text-black hover:text-gray-600 cursor-pointer"
                />
              </button>
            )}
          </div>

          {articleData ? (
            <iframe
              ref={iframeRef}
              srcDoc={articleData}
              title="Generated Content"
              sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-modals allow-downloads allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation"
              style={{
                width: "100%",
                height: "auto",
                border: "none",
                overflowY: "hidden", // Prevent vertical scrolling
              }}
              onLoad={(e) => {
                const iframe = e.target;
                if (
                  iframe &&
                  iframe.contentWindow &&
                  iframe.contentWindow.document
                ) {
                  iframe.style.height =
                    iframe.contentWindow.document.body.scrollHeight + "px";
                }
              }}
            />
          ) : (
            <div>N/A</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GeneratedTitlesPage;
